// The app's navigation, declared once. The desktop rail (AppNavRail),
// the phone menu drawer and the tab bar all render from this list, and
// `navConfig.spec.ts` walks the router to assert every `requiresAdmin`
// route is reachable from some entry (the regression guard that used to
// live on the old header dropdown, #916).
//
// Grouped by what the product is: the Brain (the wiki and its upkeep),
// the Factory (agents working in repos), Life (personal tools) and the
// System that runs it all. Each group's tone is the colour that means
// that pillar everywhere else in the UI.

export type NavTone = 'brain' | 'factory' | 'life' | 'system'
export type NavBadgeKey = 'review' | 'sessions' | 'log' | 'factory-health'
export type NavAction = 'ask' | 'log'

export interface NavItem {
  key: string
  label: string
  // SPA route. Omitted for action-only entries (Ask opens a drawer).
  to?: string
  // Rails-mounted page outside the SPA router (Jobs mission control).
  href?: string
  // Side effect the shell performs instead of / before navigating.
  action?: NavAction
  adminOnly: boolean
  badge?: NavBadgeKey
  // Extra path prefixes that should light this entry up (Review covers
  // both queue routes; Wiki covers every page and category route).
  activePrefixes?: string[]
}

export interface NavGroup {
  key: string
  label: string | null
  tone: NavTone | null
  items: NavItem[]
}

export const LOG_PAGE_PATH = 'log.md'

// The queues the Review hub tabs between (ReviewTabs). The nav's Review
// entry opens the first; the rest are reached from the tab strip.
export const REVIEW_QUEUES = [
  { key: 'lint', label: 'Lint notes', to: '/lint' },
  { key: 'synthesis', label: 'Synthesis drafts', to: '/synthesis' },
] as const

export const NAV_GROUPS: NavGroup[] = [
  {
    key: 'primary',
    label: null,
    tone: null,
    items: [
      { key: 'today', label: 'Today', to: '/', adminOnly: false },
      { key: 'ask', label: 'Ask', action: 'ask', adminOnly: false },
    ],
  },
  {
    key: 'brain',
    label: 'Brain',
    tone: 'brain',
    items: [
      { key: 'wiki', label: 'Wiki', to: '/p/index.md', adminOnly: false, activePrefixes: ['/p/', '/c/'] },
      { key: 'review', label: 'Review', to: '/lint', adminOnly: true, badge: 'review', activePrefixes: ['/synthesis'] },
      { key: 'memory', label: 'Memory', to: '/memory', adminOnly: true },
      { key: 'output-skills', label: 'Output skills', to: '/output-skills', adminOnly: true },
      { key: 'new-page', label: 'New page', to: '/p/new', adminOnly: true },
      { key: 'log', label: 'Log', to: '/log', action: 'log', adminOnly: false, badge: 'log' },
    ],
  },
  {
    key: 'factory',
    label: 'Factory',
    tone: 'factory',
    items: [
      { key: 'sessions', label: 'Sessions', to: '/agent', adminOnly: true, badge: 'sessions' },
      { key: 'repos', label: 'Repos', to: '/admin/agent-repos', adminOnly: true, badge: 'factory-health' },
      { key: 'product-owner-runs', label: 'Product-owner runs', to: '/admin/product-owner-runs', adminOnly: true },
      { key: 'prompts', label: 'Prompt library', to: '/admin/agent-prompts', adminOnly: true },
      { key: 'channels', label: 'Channels', to: '/channels', adminOnly: true },
      {
        key: 'expert-consultations',
        label: 'Expert consultations',
        to: '/admin/expert-consultations',
        adminOnly: true,
      },
    ],
  },
  {
    key: 'life',
    label: 'Life',
    tone: 'life',
    items: [
      { key: 'reminders', label: 'Reminders', to: '/reminders', adminOnly: true },
      { key: 'lists', label: 'Lists', to: '/lists', adminOnly: true },
      { key: 'calendar', label: 'Calendar', to: '/admin/calendar', adminOnly: true },
      { key: 'cv', label: 'Live CV', to: '/admin/cv', adminOnly: true },
    ],
  },
  {
    key: 'system',
    label: 'System',
    tone: 'system',
    items: [
      { key: 'usage', label: 'Usage', to: '/usage', adminOnly: true },
      { key: 'host', label: 'Host', to: '/admin/host-stats', adminOnly: true },
      { key: 'jobs', label: 'Jobs', href: '/jobs/', adminOnly: true },
      { key: 'retrieval-bench', label: 'Retrieval bench', to: '/admin/retrieval-bench', adminOnly: true },
      { key: 'tokens', label: 'API tokens', to: '/admin/tokens', adminOnly: true },
      { key: 'shares', label: 'Shares', to: '/admin/shares', adminOnly: true },
    ],
  },
]

/** The groups a user may see — admin-only entries dropped, empty groups removed. */
export function visibleNavGroups(isAdmin: boolean, groups: NavGroup[] = NAV_GROUPS): NavGroup[] {
  return groups
    .map((group) => ({ ...group, items: group.items.filter((item) => isAdmin || !item.adminOnly) }))
    .filter((group) => group.items.length > 0)
}

// Exact matches outrank any prefix match, so `/p/log.md` lights Log, not Wiki.
const EXACT_MATCH_WEIGHT = 10_000

function matchScore(item: NavItem, path: string): number {
  let score = 0
  if (item.to) {
    if (path === item.to) return EXACT_MATCH_WEIGHT
    if (item.to !== '/' && path.startsWith(`${item.to}/`)) score = item.to.length
  }
  for (const prefix of item.activePrefixes ?? []) {
    if (path.startsWith(prefix)) score = Math.max(score, prefix.length)
  }
  return score
}

/** The key of the single nav entry the current path belongs to, or null. */
export function activeNavKey(path: string, groups: NavGroup[] = NAV_GROUPS): string | null {
  let best: { key: string; score: number } | null = null
  for (const item of groups.flatMap((group) => group.items)) {
    const score = matchScore(item, path)
    if (score > 0 && (!best || score > best.score)) best = { key: item.key, score }
  }
  return best?.key ?? null
}

/** The group key the active entry lives in — drives the phone tab bar. */
export function activeGroupKey(path: string, groups: NavGroup[] = NAV_GROUPS): string | null {
  const key = activeNavKey(path, groups)
  if (!key) return null
  return groups.find((group) => group.items.some((item) => item.key === key))?.key ?? null
}
